import { useQuery } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { MetricCard } from "@/components/metric-card";
import { DataTable } from "@/components/data-table";
import { DashboardSkeleton } from "@/components/loading-skeleton";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  DollarSign,
  Users,
  FileText,
  ShoppingCart,
  ArrowLeft,
  AlertCircle,
} from "lucide-react";
import type { ProductMetrics, CampaignMetrics, SalesReportData } from "@re/shared";
import { getSelectedPipelines } from "./settings";

export default function Products() {
  const params = useParams<{ id?: string }>();
  const [, navigate] = useLocation();
  const productId = params.id;

  const { data, isLoading, error } = useQuery<SalesReportData>({
    queryKey: ["/api/report"],
  });

  if (isLoading) {
    return (
      <div className="p-6">
        <DashboardSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-semibold mb-2">Failed to load data</h3>
            <p className="text-muted-foreground text-center">
              Please try again later.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const selectedPipelineIds = getSelectedPipelines();
  const filteredProducts = selectedPipelineIds.length > 0
    ? data.products.filter(p => selectedPipelineIds.includes(p.pipelineId))
    : data.products;

  if (productId) {
    const product = data.products.find((p) => p.pipelineId === productId);

    if (!product) {
      return (
        <div className="p-6">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Product not found</h3>
              <Button onClick={() => navigate("/products")} data-testid="button-back-products">
                Back to Products
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return <ProductDetail product={product} />;
  }

  const productColumns = [
    {
      key: "pipelineName",
      header: "Product",
      render: (item: ProductMetrics) => (
        <span className="font-medium">{item.pipelineName}</span>
      ),
    },
    {
      key: "conversions",
      header: "Opportunities",
      sortable: true,
      align: "right" as const,
      render: (item: ProductMetrics) => item.conversions.toLocaleString(),
    },
    {
      key: "quotes",
      header: "Active",
      sortable: true,
      align: "right" as const,
      render: (item: ProductMetrics) => (
        <div className="flex items-center justify-end gap-2">
          <span>{item.quotes.toLocaleString()}</span>
          <Badge variant="secondary" className="text-xs">
            {item.quoteRate.toFixed(0)}%
          </Badge>
        </div>
      ),
    },
    {
      key: "sales",
      header: "Completed",
      sortable: true,
      align: "right" as const,
      render: (item: ProductMetrics) => (
        <div className="flex items-center justify-end gap-2">
          <span>{item.sales.toLocaleString()}</span>
          <Badge variant="secondary" className="text-xs">
            {item.saleRate.toFixed(0)}%
          </Badge>
        </div>
      ),
    },
    {
      key: "revenue",
      header: "Quoted Value",
      sortable: true,
      align: "right" as const,
      render: (item: ProductMetrics) => `£${item.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Products</h1>
        <p className="text-muted-foreground">
          Performance metrics by product pipeline
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Products</CardTitle>
          <CardDescription>
            Click on a product to view campaign details
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            data={filteredProducts}
            columns={productColumns}
            onRowClick={(item) => navigate(`/products/${item.pipelineId}`)}
            showDrilldown
          />
        </CardContent>
      </Card>
    </div>
  );
}

function ProductDetail({ product }: { product: ProductMetrics }) {
  const [, navigate] = useLocation();

  const campaignColumns = [
    {
      key: "campaignName",
      header: "Engineer",
      render: (item: CampaignMetrics) => (
        <div>
          <span className="font-medium">{item.campaignName}</span>
          <Badge variant="outline" className="ml-2 text-xs">
            {item.source}
          </Badge>
        </div>
      ),
    },
    {
      key: "conversions",
      header: "Opportunities",
      sortable: true,
      align: "right" as const,
      render: (item: CampaignMetrics) => item.conversions.toLocaleString(),
    },
    {
      key: "quotes",
      header: "Active",
      sortable: true,
      align: "right" as const,
      render: (item: CampaignMetrics) => (
        <div className="flex items-center justify-end gap-2">
          <span>{item.quotes.toLocaleString()}</span>
          <Badge variant="secondary" className="text-xs">
            {item.quoteRate.toFixed(0)}%
          </Badge>
        </div>
      ),
    },
    {
      key: "sales",
      header: "Completed",
      sortable: true,
      align: "right" as const,
      render: (item: CampaignMetrics) => (
        <div className="flex items-center justify-end gap-2">
          <span>{item.sales.toLocaleString()}</span>
          <Badge variant="secondary" className="text-xs">
            {item.saleRate.toFixed(0)}%
          </Badge>
        </div>
      ),
    },
    {
      key: "revenue",
      header: "Quoted Value",
      sortable: true,
      align: "right" as const,
      render: (item: CampaignMetrics) => `£${item.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-4">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/products">Products</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{product.pipelineName}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/products")}
            data-testid="button-back"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {product.pipelineName}
            </h1>
            <p className="text-muted-foreground">
              Campaign performance for this product
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Opportunities"
          value={product.conversions}
          icon={<Users className="h-4 w-4" />}
        />
        <MetricCard
          title="Active Jobs"
          value={product.quotes}
          subtitle={`${product.quoteRate.toFixed(0)}% of total`}
          icon={<FileText className="h-4 w-4" />}
        />
        <MetricCard
          title="Completed"
          value={product.sales}
          subtitle={`${product.saleRate.toFixed(0)}% completion rate`}
          icon={<ShoppingCart className="h-4 w-4" />}
        />
        <MetricCard
          title="Quoted Value"
          value={`£${product.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          icon={<DollarSign className="h-4 w-4" />}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Campaigns</CardTitle>
          <CardDescription>
            Individual campaign performance for {product.pipelineName}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            data={product.campaigns}
            columns={campaignColumns}
            onRowClick={(item) => navigate(`/campaigns/${item.campaignId}`)}
            showDrilldown
          />
        </CardContent>
      </Card>
    </div>
  );
}
